"use client";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Search,
  Palette,
  Layout,
  HelpCircle,
  AlertCircle,
  Trash2,
  Eye,
  FileText,
} from "lucide-react";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { SettingsButton } from "../ui/elements/SettingsButton";
import { SectionCategoryCard } from "../features/sections/SectionCategoryCard";
import { ComponentPreview } from "../features/sections/ComponentPreview";
import { SeoSettingsPanel } from "../ui/panels/SeoSettingsPanel";
import { SiteStylePanel } from "../ui/panels/SiteStylePanel";
import { DraftsPanel } from "../ui/panels/DraftsPanel";
import { SectionPanel } from "../features/sections/SectionPanel";
import { AnimatedSidebarPanel } from "./AnimatedSidebarPanel";
import {
  globalComponents,
  customComponents,
  ComponentVariant,
} from "../config/componentConfig";
import { useSectionCategories } from "@/hooks/useSectionCategories";
import { SectionCategoryService } from "@/services/sectionCategoryService";
import { Section, SectionCategory } from "@/types/models/page";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import {
  selectSectionsArray as selectSectionsArraySelector,
  selectSelectedSection as selectSelectedSectionSelector,
  selectPreviewHiddenSectionIds,
} from "@/store/selectors/webbuilder.selectors";
import {
  setSelectedSection,
  reorderSections,
  addSection,
  toggleSectionPreviewVisibility,
} from "@/store/slices/webbuilderSlice";

interface SidebarProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  showMainContent?: boolean;
  pageId?: string; // Current page ID for SEO settings
  currentPage?: any; // Current page object for SEO settings
  onDeletePage?: (pageId: string) => void; // Delete page handler
}

export function Sidebar({
  sidebarCollapsed,
  setSidebarCollapsed,
  activeTab,
  setActiveTab,
  showMainContent = true,
  pageId,
  currentPage,
  onDeletePage,
}: SidebarProps) {
  const params = useParams();
  const dispatch = useAppDispatch();
  const [activeSection, setActiveSection] = useState<"global" | "custom">(
    "global",
  );
  const [showComponentPanel, setShowComponentPanel] = useState(false);
  const [showSectionPanel, setShowSectionPanel] = useState(false);
  const [showDraftsPanel, setShowDraftsPanel] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSectionCategoryId, setSelectedSectionCategoryId] = useState<
    string | null
  >(null);
  const [selectedSectionCategoryName, setSelectedSectionCategoryName] =
    useState<string | null>(null);
  const [showSeoPanel, setShowSeoPanel] = useState(false);
  const [showSiteStylePanel, setShowSiteStylePanel] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  // Get sections and selected section from Redux store
  const sectionsFromStore = useAppSelector(
    selectSectionsArraySelector,
  ) as Section[];
  const selectedSection = useAppSelector(
    selectSelectedSectionSelector,
  ) as Section | null;
  const previewHiddenSectionIds = useAppSelector(
    selectPreviewHiddenSectionIds,
  ) as string[];

  // Show all sections in the layers panel
  const sections = sectionsFromStore;

  const navigate = useNavigate();

  // Section categories hook
  const {
    categories: sectionCategories,
    loading: categoriesLoading,
    error: categoriesError,
    fetchCategories,
  } = useSectionCategories();

  // Custom categories state
  const [customCategories, setCustomCategories] = useState<SectionCategory[]>(
    [],
  );
  const [customCategoriesLoading, setCustomCategoriesLoading] = useState(false);
  const [customCategoriesError, setCustomCategoriesError] = useState<
    string | null
  >(null);

  const tabs = ["Settings", "Sections", "Layer"];

  // Fetch section categories on component mount
  useEffect(() => {
    if (activeTab === "Sections") {
      if (activeSection === "global") {
        console.log("🌍 Fetching global section categories...");
        fetchCategories(1, 50); // Fetch more categories to avoid pagination in sidebar
      } else {
        console.log("🏪 Fetching custom section categories...");
        fetchCustomCategories();
      }
    }
  }, [activeTab, activeSection, fetchCategories]);

  useEffect(() => {
    console.log("📊 Section categories state updated:", {
      count: sectionCategories?.length || 0,
      loading: categoriesLoading,
      error: categoriesError,
      categories: sectionCategories,
    });
  }, [sectionCategories, categoriesLoading, categoriesError]);

  const fetchCustomCategories = async () => {
    setCustomCategoriesLoading(true);
    setCustomCategoriesError(null);
    try {
      const response = await SectionCategoryService.getCustomSectionCategories(
        1,
        50,
      );
      setCustomCategories(response.data as SectionCategory[]);
    } catch (error) {
      console.error("Failed to fetch custom categories:", error);
      setCustomCategoriesError(
        error instanceof Error
          ? error.message
          : "Failed to load custom categories",
      );
    } finally {
      setCustomCategoriesLoading(false);
    }
  };

  const openSectionPanel = (categoryId: string) => {
    const category = sectionCategories?.find((cat) => cat.id === categoryId);
    setSelectedSectionCategoryId(categoryId);
    setSelectedSectionCategoryName(category?.name || "Sections");
    setShowSectionPanel(true);
  };

  const closeComponentPanel = () => {
    setShowComponentPanel(false);
    setSelectedCategory(null);
  };

  const closeSectionPanel = () => {
    setShowSectionPanel(false);
    setSelectedSectionCategoryId(null);
    setSelectedSectionCategoryName(null);
  };

  const closeSeoPanel = () => {
    setShowSeoPanel(false);
  };

  const closeSiteStylePanel = () => {
    setShowSiteStylePanel(false);
  };

  const closeDraftsPanel = () => {
    setShowDraftsPanel(false);
  };

  const handleDeletePage = () => {
    if (!pageId || !onDeletePage) return;
    onDeletePage(pageId);
  };

  const handleComponentSelect = (variant: ComponentVariant) => {
    console.log("Adding component to canvas:", variant);
  };

  const handleSectionSelect = (section: Section) => {
    console.log("Adding section to page:", section);
    // Add the section to the Redux store
    dispatch(addSection(section));
    closeSectionPanel();
  };

  // Handle section selection from layers panel
  const handleLayerSectionSelect = (section: Section) => {
    // Allow selection of locked sections so users can unlock them
    dispatch(setSelectedSection(section));
  };

  // Handle re-enabling hidden sections
  const handleReEnableSection = (sectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleSectionPreviewVisibility(sectionId));
  };

  // Handle drag and drop for layers
  const handleDragStart = (e: React.DragEvent, sectionId: string) => {
    setDraggedItem(sectionId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", sectionId);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    // Only set drop target if it's different from the dragged item
    if (draggedItem && draggedItem !== targetId) {
      setDropTarget(targetId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the container, not moving between child elements
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDropTarget(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();

    if (!draggedItem || draggedItem === targetId) {
      setDraggedItem(null);
      setDropTarget(null);
      return;
    }

    // Dispatch the reorder action to Redux store
    dispatch(reorderSections({ draggedId: draggedItem, targetId }));

    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  // Get section icon based on section type or category
  const getSectionIcon = (section: Section) => {
    // You can customize this based on section type or category
    const iconMap: Record<string, string> = {
      hero: "🎯",
      header: "📄",
      footer: "📄",
      content: "📝",
      gallery: "🖼️",
      contact: "📞",
      testimonial: "💬",
      pricing: "💰",
      features: "✨",
      cta: "🚀",
      navigation: "🧭",
      banner: "🎪",
      form: "📋",
      video: "🎥",
      map: "🗺️",
      social: "📱",
      newsletter: "📧",
      faq: "❓",
      team: "👥",
      blog: "📰",
    };

    const sectionType =
      section.section_identifier?.toLowerCase() || section.type?.toLowerCase();
    return iconMap[sectionType] || "📄";
  };

  // Get section display name
  const getSectionDisplayName = (section: Section) => {
    return section.name || section.section_identifier || "Untitled Section";
  };

  // Get section category name
  const getSectionCategoryName = (section: Section) => {
    return section.category?.name || "Uncategorized";
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "Settings":
        return (
          <div className="flex flex-col h-full space-y-4">
            <div>
              <h3 className="text-sm font-medium">Page Settings</h3>
              <p className="text-xs text-muted-foreground">
                Configure page properties and global settings
              </p>
            </div>

            <div className="space-y-3">
              <SettingsButton
                icon={Search}
                label="Page SEO Settings"
                onClick={() => setShowSeoPanel(true)}
              />
              <SettingsButton
                icon={Palette}
                label="Site Style"
                onClick={() => setShowSiteStylePanel(true)}
              />
              {pageId ? (
                <SettingsButton
                  icon={FileText}
                  label="Manage Drafts"
                  onClick={() => setShowDraftsPanel(true)}
                />
              ) : (
                <div className="p-3 bg-muted dark:bg-auth-background border border-border dark:border-auth-background rounded-lg opacity-50">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      No page selected
                    </span>
                  </div>
                </div>
              )}
              {pageId && onDeletePage && (
                <SettingsButton
                  icon={Trash2}
                  label="Delete Page"
                  onClick={handleDeletePage}
                  variant="destructive"
                />
              )}
            </div>

            <div className="flex-1"></div>

            <div className="space-y-4">
              <div className="flex justify-center">
                <span className="bg-muted dark:bg-auth-border px-3 py-1 rounded-full text-xs text-muted-foreground">
                  Need Help?
                </span>
              </div>

              <SettingsButton
                icon={HelpCircle}
                label="Need Help"
                onClick={() => console.log("Help clicked")}
              />
            </div>
          </div>
        );

      case "Sections":
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium">Section Categories</h3>
              <p className="text-xs text-muted-foreground">
                Browse and select sections by category
              </p>
            </div>

            {/* Tab Layout */}
            <div className="flex bg-muted dark:bg-auth-background rounded-lg p-1">
              <button
                onClick={() => setActiveSection("global")}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeSection === "global"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Global
              </button>
              <button
                onClick={() => setActiveSection("custom")}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeSection === "custom"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Custom
              </button>
            </div>

            {/* Global Categories */}
            {activeSection === "global" && (
              <>
                {categoriesLoading && (
                  <div className="flex flex-col items-center justify-center py-8 space-y-3">
                    <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-muted-foreground">
                        Loading categories...
                      </p>
                    </div>
                  </div>
                )}

                {categoriesError && (
                  <div className="flex flex-col items-center justify-center py-8 space-y-3">
                    <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-destructive" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-destructive">
                        Failed to load categories
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {categoriesError}
                      </p>
                    </div>
                  </div>
                )}

                {!categoriesLoading && !categoriesError && (
                  <>
                    {sectionCategories && sectionCategories.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {sectionCategories.map((category) => (
                          <SectionCategoryCard
                            key={category.id}
                            category={category}
                            onClick={() => openSectionPanel(category.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 space-y-3">
                        <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                          <Layout className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-muted-foreground">
                            No categories found
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            No section categories are available yet
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Custom Categories */}
            {activeSection === "custom" && (
              <>
                {customCategoriesLoading && (
                  <div className="flex flex-col items-center justify-center py-8 space-y-3">
                    <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-muted-foreground">
                        Loading custom categories...
                      </p>
                    </div>
                  </div>
                )}

                {customCategoriesError && (
                  <div className="flex flex-col items-center justify-center py-8 space-y-3">
                    <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-destructive" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-destructive">
                        Failed to load custom categories
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {customCategoriesError}
                      </p>
                    </div>
                  </div>
                )}

                {!customCategoriesLoading && !customCategoriesError && (
                  <>
                    {customCategories && customCategories.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {customCategories.map((category) => (
                          <SectionCategoryCard
                            key={
                              category.id || `uncategorized-${Math.random()}`
                            }
                            category={category}
                            onClick={() => openSectionPanel(category.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 space-y-3">
                        <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                          <Layout className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-muted-foreground">
                            No custom categories found
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            No custom section categories are available yet
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        );

      case "Layer":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">Layers</h3>
              <span className="text-xs text-muted-foreground bg-muted dark:bg-auth-background border border-border dark:border-auth-border px-2 py-1 rounded-full">
                {sections.length} section{sections.length !== 1 ? "s" : ""}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Manage page structure and element hierarchy
            </p>
            <div className="space-y-2">
              <div className="p-3 bg-muted/50 dark:bg-auth-background/50 border border-border dark:border-auth-border rounded-lg">
                <div className="font-medium text-xs mb-3 flex items-center text-foreground">
                  <span className="mr-2">🗂️</span>
                  <span>Page</span>
                </div>
                <div className="space-y-1">
                  {sections.length > 0 ? (
                    sections.map((section) => {
                      const isSelected = selectedSection?.id === section.id;
                      const icon = getSectionIcon(section);
                      const displayName = getSectionDisplayName(section);
                      const categoryName = getSectionCategoryName(section);

                      return (
                        <div key={section.id}>
                          <div
                            className={`p-2 bg-card dark:bg-auth-card hover:bg-accent dark:hover:bg-auth-background rounded-md cursor-pointer border transition-all duration-200 flex items-center group ${
                              isSelected
                                ? "border-primary bg-primary/5 dark:bg-primary/10 shadow-sm"
                                : "border-border dark:border-auth-border hover:border-primary/30 dark:hover:border-primary/30"
                            } ${draggedItem === section.id ? "opacity-50 scale-95" : ""} ${dropTarget === section.id ? "border-primary dark:border-primary ring-2 ring-primary/20" : ""} ${section.is_hidden ? "opacity-50" : ""}`}
                            draggable={!section.is_locked}
                            onDragStart={(e) => handleDragStart(e, section.id)}
                            onDragOver={(e) => handleDragOver(e, section.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, section.id)}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLayerSectionSelect(section);
                            }}
                            title={`${displayName} (${categoryName})${section.is_locked ? " - Locked" : ""}${section.is_hidden ? " - Hidden" : ""}`}
                          >
                            <span className="opacity-0 group-hover:opacity-100 mr-1.5 text-muted-foreground transition-opacity text-xs cursor-grab active:cursor-grabbing">
                              ⋮⋮
                            </span>
                            <span className="mr-2 text-sm flex-shrink-0">
                              {icon}
                            </span>
                            <div className="flex-1 text-left min-w-0">
                              <div className="font-medium text-xs truncate text-foreground">
                                {displayName}
                              </div>
                              <div className="text-[10px] text-muted-foreground truncate">
                                {categoryName}
                              </div>
                            </div>
                            <div className="flex items-center space-x-1 ml-2">
                              {section.is_locked && (
                                <span
                                  className="text-amber-600 dark:text-amber-500 text-xs"
                                  title="Locked"
                                >
                                  🔒
                                </span>
                              )}
                              {/* Eye button to toggle preview visibility */}
                              <button
                                onClick={(e) =>
                                  handleReEnableSection(section.id, e)
                                }
                                className="text-muted-foreground hover:text-primary dark:hover:text-primary p-1 rounded transition-opacity"
                                title={
                                  previewHiddenSectionIds.includes(section.id)
                                    ? "Show in preview"
                                    : "Hide from preview"
                                }
                              >
                                {previewHiddenSectionIds.includes(
                                  section.id,
                                ) ? (
                                  <Eye className="w-3 h-3 opacity-40" />
                                ) : (
                                  <Eye className="w-3 h-3" />
                                )}
                              </button>
                              {isSelected && (
                                <span className="text-primary text-xs">✓</span>
                              )}
                            </div>
                          </div>

                          {/* Drop zone indicator below each item */}
                          {dropTarget === section.id &&
                            draggedItem &&
                            draggedItem !== section.id && (
                              <div className="h-0.5 bg-primary dark:bg-primary rounded-full mt-1 animate-pulse shadow-sm shadow-primary/50"></div>
                            )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 space-y-3">
                      <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center border border-border dark:border-auth-border">
                        <Layout className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-foreground">
                          No sections yet
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Add sections from the Sections tab to see them here
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  /** Always exit to store dashboard — do not use browser history (avoids landing on onboarding again). */
  const back = () => {
    const storeType = params?.storeType as string | undefined;
    const storeId = params?.storeId as string | undefined;
    if (storeType && storeId) {
      navigate(`/${storeType}/${storeId}/dashboard`);
      return;
    }
    navigate(`/auth/store-selector`);
  };

  return (
    <div
      className={`relative h-full transition-all duration-700 ease-out z-10 ${
        showMainContent
          ? "translate-x-0 opacity-100"
          : "-translate-x-full opacity-0"
      }`}
    >
      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="absolute top-20 left-full z-20 w-8 h-12 bg-card dark:bg-auth-card border-r border-t border-b border-border dark:border-auth-background rounded-r-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors shadow-theme-md"
        aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        style={{ marginLeft: sidebarCollapsed ? "0px" : "-4px" }}
      >
        {sidebarCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      {/* Sidebar Content — avoid overflow-hidden when expanded or it clips the page dropdown menu */}
      <div
        className={`${
          sidebarCollapsed ? "w-0 overflow-hidden" : "w-64 overflow-visible"
        } h-full bg-card dark:bg-auth-card border-r border-border dark:border-auth-background flex flex-col transition-[width] duration-300 animate-slideInLeft relative shadow-theme-sidebar`}
      >
        {/* Main Sidebar Content */}
        <div className="flex h-full min-h-0 w-64 flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-border dark:border-auth-background flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ButtonOutline
                onClick={() => back()}
                className="dark:bg-auth-background dark:border-auth-background hover:bg-primary hover:text-primary-foreground h-8 w-8 p-0 flex items-center justify-center transition-colors"
                aria-label="Back to dashboard"
              >
                <ArrowLeft className="w-4 h-4" />
              </ButtonOutline>
              <h2 className="text-lg font-semibold">Web Builder Tools</h2>
            </div>
          </div>

          {/* Tabs */}
          <div className="p-3">
            <div className="flex bg-muted dark:bg-auth-background rounded-full p-1 border border-border dark:border-auth-background">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    closeComponentPanel();
                    closeSectionPanel();
                    closeSeoPanel();
                    closeSiteStylePanel();
                    closeDraftsPanel();
                  }}
                  className={`flex-1 px-3 py-2 text-xs font-medium transition-all duration-200 rounded-full ${
                    activeTab === tab
                      ? "bg-primary text-primary-foreground shadow-theme-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {renderTabContent()}
          </div>
        </div>

        {/* Component Panel */}
        <AnimatedSidebarPanel
          isOpen={showComponentPanel}
          onClose={closeComponentPanel}
          title={selectedCategory || "Components"}
          subtitle="Choose a component to add"
        >
          <div className="p-4">
            {selectedCategory && (
              <div className="space-y-3">
                {(() => {
                  const components =
                    activeSection === "global"
                      ? globalComponents
                      : customComponents;
                  const category = components[selectedCategory];
                  return category?.variants.map((variant: ComponentVariant) => (
                    <ComponentPreview
                      key={variant.name}
                      name={variant.name}
                      preview={variant.preview}
                      description={variant.description}
                      icon={category.icon}
                      onSelect={() => handleComponentSelect(variant)}
                      onDragStart={(e: React.DragEvent) => {
                        e.dataTransfer.setData("text/plain", variant.name);
                      }}
                    />
                  ));
                })()}
              </div>
            )}
          </div>
        </AnimatedSidebarPanel>

        {/* Section Panel */}
        <AnimatedSidebarPanel
          isOpen={showSectionPanel}
          onClose={closeSectionPanel}
          title={selectedSectionCategoryName || "Sections"}
          subtitle="Choose a section to add"
        >
          {selectedSectionCategoryId && (
            <SectionPanel
              categoryId={selectedSectionCategoryId}
              onSectionSelect={handleSectionSelect}
            />
          )}
        </AnimatedSidebarPanel>

        {/* SEO Settings Panel */}
        <SeoSettingsPanel
          isOpen={showSeoPanel}
          onClose={closeSeoPanel}
          pageId={pageId}
          currentPage={currentPage}
        />

        {/* Site Style Panel */}
        <SiteStylePanel
          isOpen={showSiteStylePanel}
          onClose={closeSiteStylePanel}
        />

        {/* Drafts Panel */}
        <DraftsPanel
          isOpen={showDraftsPanel}
          onClose={closeDraftsPanel}
          pageId={pageId}
        />
      </div>
    </div>
  );
}
